import { AbstractControl } from '@angular/forms';
import DOMPurify from 'dompurify';

const SANITIZE_CACHE_LIMIT = 500;
const sanitizeCache: Map<string, string> = new Map<string, string>();
const suspiciousContentPattern =
  /<|>|&(?:#\d+|#x[a-f0-9]+|[a-z]+);|javascript:|on\w+\s*=|data:text\/html/i;

function shouldSanitize(value: string): boolean {
  return suspiciousContentPattern.test(value);
}

function sanitizeTextValue(value: string): string {
  // Fast path: avoid DOMPurify work for normal plain text input.
  if (!value || !shouldSanitize(value)) {
    return value;
  }

  const fromCache = sanitizeCache.get(value);
  if (typeof fromCache === 'string') {
    return fromCache;
  }

  const sanitized = DOMPurify.sanitize(value, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
  });

  if (sanitizeCache.size >= SANITIZE_CACHE_LIMIT) {
    sanitizeCache.clear();
  }
  sanitizeCache.set(value, sanitized);

  return sanitized;
}

export function sanitizePotentialXss(value: unknown): unknown {
  if (typeof value === 'string') {
    return sanitizeTextValue(value);
  }

  if (Array.isArray(value)) {
    return value.map((entry) => sanitizePotentialXss(entry));
  }

  if (value instanceof Date || value instanceof File) {
    return value;
  }

  if (value && typeof value === 'object') {
    return Object.entries(value).reduce(
      (sanitized, [key, entry]) => {
        sanitized[key] = sanitizePotentialXss(entry);
        return sanitized;
      },
      {} as Record<string, unknown>
    );
  }

  return value;
}

export function applyXssValidation(control: AbstractControl): boolean {
  const nestedControls = (control as any)?.controls;

  if (nestedControls && typeof nestedControls === 'object') {
    const children: AbstractControl[] = Array.isArray(nestedControls)
      ? nestedControls
      : Object.values(nestedControls);

    return children.some((entry: AbstractControl) => applyXssValidation(entry));
  }

  if (control.disabled || typeof control.value !== 'string') {
    return false;
  }

  const existingErrors = control.errors || {};
  const value = control.value;

  if (!value.trim()) {
    if (existingErrors['xssContent']) {
      const { xssContent, ...rest } = existingErrors;
      control.setErrors(Object.keys(rest).length ? rest : null);
    }
    return false;
  }

  const sanitized = sanitizePotentialXss(value);
  const hasXss = typeof sanitized === 'string' && sanitized !== value;

  if (hasXss) {
    control.setErrors({
      ...existingErrors,
      xssContent: true,
    });
    return true;
  }

  if (existingErrors['xssContent']) {
    const { xssContent, ...rest } = existingErrors;
    control.setErrors(Object.keys(rest).length ? rest : null);
  }

  return false;
}
